import { useEffect, useState, useCallback, useRef } from "react";
import { AiReadReport } from "./AiReadReport";
import { AiValueTick } from "./AiValueTick";
import { PendingComponentBadge } from "./PendingComponentBadge";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthImg } from "@/components/AuthImg";
import { PhotoStage, PhotoControls, usePhotoGestures } from "@/components/PhotoStage";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogHeader } from "@/components/ui/dialog";
import {
  Sparkles, Loader2, Check, CheckCheck, AlertTriangle, X, PackagePlus, Wand2, Mic, MicOff,
  Copy, ScanText, FileText, DollarSign, Camera, ChevronRight, ChevronLeft, Info, Boxes,
  ThumbsUp, ThumbsDown, Maximize2, RotateCw, ImageOff, Columns2, Eye, ShieldCheck, ListChecks, Search,
} from "lucide-react";
import { useVehicleCategories } from "@/lib/vehicleLists";
import { setDonorCategory } from "@/lib/setDonorCategory";
import { CreatePartFromTruck } from "@/components/CreatePartFromTruck";
import { BuildSheetViewer } from "@/components/BuildSheetViewer";
import { PreflightBanner } from "@/components/PreflightBanner";
import { PresenceReviewBlock } from "@/components/PresenceReviewBlock";

const money = (n) => (n == null || n === "" ? "—" : `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);
const SCAN_POLL_MS = 3000;

// ONE full "Ask AI" truck workbench — mirrors the parts AiWorkbench + truck extras.
// Photo picker (20-photo safe batches), transparency panel, staff hint (+voice), VIN-decode &
// "from your other trucks" prefill, one-call fill w/ Use/confirm, confidence-grouped confirm,
// click-to-edit searchable serials → spawn part, inline dup-serial catch, cost-walled VALUE
// ballpark, internal DESCRIPTION, gap re-scan, export, cost meter. Non-destructive; donor tie safe.
export function TruckAiWorkbench({ open, onOpenChange, donorId, donor, onDone }) {
  const { has } = useAuth();
  const categories = useVehicleCategories();
  const isAdmin = has("manage_ai_budget");
  const showCost = has("view_acquisition_cost");
  const canParts = has("manage_parts");

  const [data, setData] = useState(null);        // /specs groups
  const [draft, setDraft] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const [includeBuildSheet, setIncludeBuildSheet] = useState(true);
  const [hint, setHint] = useState("");
  const [result, setResult] = useState(null);
  const [vinDecode, setVinDecode] = useState(null);
  const [cpt, setCpt] = useState(null);
  const [listening, setListening] = useState(false);
  const [readReport, setReadReport] = useState(null);
  const [scan, setScan] = useState(null);           // server-side scan job: {status, read, total, …}
  const [kicking, setKicking] = useState(false);
  const [groupFb, setGroupFb] = useState({});       // {component: 'correct'|'wrong'} — one-tap grouping feedback
  const [normalizedText, setNormalizedText] = useState("");
  const markOption = async (o) => {
    const now = o.on_this_truck === false;
    if (!window.confirm(now
      ? `Mark ${o.code} as ON this truck again?`
      : `Mark ${o.code} — ${o.description} — as NOT on this truck? The code stays exactly as the sheet printed it; it is only marked.`)) return;
    try {
      const { data: res } = await api.post(`/donors/${donorId}/build-options/mark`,
        { code: o.code, description: o.description || "", on_this_truck: now });
      toast.success(res.message);
      await loadSpecs();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "That could not be marked."); }
  };
  const [normalizing, setNormalizing] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  // WHETHER THE SHEET ACTUALLY SAID ANYTHING. The server judges it (one shared definition) and
  // this holds its verdict so the screen can stop calling a near-empty read a success.
  const [thinSheet, setThinSheet] = useState(null);
  const [landedNothing, setLandedNothing] = useState(null);
  const [rawText, setRawText] = useState("");
  const [showRaw, setShowRaw] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);   // {fileId, label} for A1/A2 "view source photo"
  const [showOptions, setShowOptions] = useState(false);
  const [extraDraft, setExtraDraft] = useState({});   // {component: {label, value}} for human quick-add
  const recRef = useRef(null);

  // THE PHOTO STAGE — the one shared surface (zoom / rotate / drag / pinch), not a second copy of
  // it. This panel used to carry its own, which is how the truck screens drifted (2026-10-15).
  const [active, setActive] = useState(0);
  const photoG = usePhotoGestures(active);
  const [fullIdx, setFullIdx] = useState(null);
  const resetView = () => photoG.reset();
  const stepPhoto = (dir) => { setActive((i) => (i + dir + photos.length) % photos.length); resetView(); };

  const sendGroupFeedback = async (comp, verdict, numbers) => {
    setGroupFb((m) => ({ ...m, [comp]: verdict }));   // optimistic; one tap per component
    try {
      await api.post(`/donors/${donorId}/component-feedback`, { component: comp, verdict, numbers });
      toast.success(verdict === "correct" ? "Thanks — grouping marked right" : "Thanks — flagged for review");
    } catch (e) {
      setGroupFb((m) => { const n = { ...m }; delete n[comp]; return n; });
      toast.error(formatApiErrorDetail(e) || "Could not record feedback");
    }
  };

  const photos = (donor?.photos || []).filter((p) => p?.file_id && !p.is_archived);
  const hasBuildSheet = !!donor?.build_sheet?.file_id;
  // A SHEET RUN THAT LEFT THE TRUCK STILL EMPTY. The server judges it against the same
  // recorded-spec definition the thin-capture worklist uses, so a re-extract on an
  // already-captured truck (which legitimately fills nothing new) is never flagged.
  const thinResult = !!result?.thin_result;
  // THE TICKS COME FROM THE SERVER, not from this component's memory. They used to live in a
  // React Set, so closing the dialog forgot which photos had been read and the next scan paid
  // for them again. Each photo now carries its own `ai_read_at`.
  const readIds = new Set(photos.filter((p) => p.ai_read_at).map((p) => p.file_id));
  const unreadCount = photos.length - readIds.size;
  const scanRunning = scan?.status === "running";
  const scanning = scanRunning || kicking;

  const loadSpecs = useCallback(async () => {
    try { const r = await api.get(`/donors/${donorId}/specs`); setData(r.data); setDraft({}); }
    catch { setData(null); }
  }, [donorId]);

  useEffect(() => {
    if (!open) return;
    loadSpecs();
    setResult(null); setHint(""); setIncludeBuildSheet(true);
    setReadReport(null);
    if (donor?.build_sheet?.file_id) {
      api.get(`/donors/${donorId}/ai/extract-status`)
        .then(({ data: st }) => { if (st?.status === "done") setReadReport(st.result || null); })
        .catch(() => {});
    }
    setActive(0); resetView();
    setNormalizedText(donor?.build_sheet?.normalized_text || ""); setShowTranscript(false);
    // WAS THE READ WORTH ANYTHING? Ask the server, every time the panel opens — don't trust a
    // stamp. Most sheets in the yard were read before this existed and carry no stamp at all, and
    // those are exactly the ones nobody has checked. Counts only; no AI call, no cost.
    setThinSheet(null);
    setLandedNothing(null);
    if (donor?.build_sheet?.normalized_text) {
      api.get(`/donors/${donorId}/ai/normalize-status`)
        .then(({ data: st }) => {
          setThinSheet(st?.thin
            ? { reason: st.thin_reason, chars: st.chars, pages: st.pages, perPage: st.chars_per_page }
            : null);
          setLandedNothing(st && st.landed === false
            ? { reason: st.landed_reason, specs: st.landed_specs }
            : null);
        })
        .catch(() => {});
    }
    // VIN-decode prefill (context only — zero AI cost); fold into the AI hint
    if (donor?.vin && donor.vin.length >= 11) {
      api.post("/donors/decode-vin", { vin: donor.vin }).then(({ data: dd }) => setVinDecode(dd.decoded ? dd.fields : null)).catch(() => setVinDecode(null));
    } else setVinDecode(null);
  }, [open, donorId, donor, loadSpecs]);

  // ---------- voice hint (Web Speech API, client-side, free) ----------
  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice input isn't supported on this browser."); return; }
    if (listening) { recRef.current?.stop(); return; }
    const rec = new SR(); rec.lang = "en-US"; rec.interimResults = false; rec.continuous = false;
    rec.onresult = (e) => { const t = e.results[0][0].transcript; setHint((h) => (h ? h + " " : "") + t); };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec; setListening(true); rec.start();
  };

  const buildRefine = () => {
    let refine = (hint || "").trim();
    if (vinDecode) {
      const dec = [vinDecode.year, vinDecode.make, vinDecode.model].filter(Boolean).join(" ");
      if (dec) refine = `VIN decodes to: ${dec}. ` + refine;
    }
    return refine;
  };

  // ONE WAY TO READ THE PHOTOS, for 3 photos or 121. The work happens in a background job on the
  // server: this kicks it, then polls. Walk away, close the tab, come back — the progress is the
  // job's, not this screen's, and it only ever reads the photos that have never been read.
  const pollScan = useCallback(async () => {
    try {
      const { data: st } = await api.get(`/donors/${donorId}/ai/scan-status`);
      setScan(st);
      return st;
    } catch { return null; }
  }, [donorId]);

  useEffect(() => {
    if (!open) return;
    let stop = false;
    let last = null;
    const tick = async () => {
      if (stop) return;
      const st = await pollScan();
      // The finished job's drafted fields are pulled into THIS dialog, and nothing else.
      // Telling the page behind to reload while the dialog is open re-renders the panel that
      // owns its open flag and tears the dialog down mid-job — which is exactly what happened
      // the moment a background scan finished with the window still open: the crew pressed
      // "Start again", waited, and the screen shut itself the second the work completed.
      // The page refreshes when the dialog CLOSES (Done, X, Escape, overlay) — see
      // TruckSpecsPanel's onOpenChange.
      if (last === "running" && st && st.status !== "running") await loadSpecs();
      last = st?.status;
    };
    tick();
    const id = setInterval(tick, SCAN_POLL_MS);
    return () => { stop = true; clearInterval(id); };
  }, [open, donorId, pollScan, loadSpecs]);

  const startScan = async () => {
    setKicking(true);
    try {
      const { data: st } = await api.post(`/donors/${donorId}/ai/scan-photos`, {
        refine: buildRefine(), suggest_category: needsCat, include_build_sheet: includeBuildSheet && hasBuildSheet });
      setScan(st);
      toast.success(st.total > 1
        ? `Reading ${st.unread || st.total} photo${(st.unread || st.total) === 1 ? "" : "s"} in the background — you can close this and come back.`
        : "Reading in the background — you can close this and come back.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setKicking(false); }
  };

  // ---- Two-step build sheet: NORMALIZE (transcribe any format) → EXTRACT (from clean text) ----
  const normalize = async () => {
    setNormalizing(true);
    try {
      const { data: kick } = await api.post(`/donors/${donorId}/ai/normalize-build-sheet`, {});
      if (kick.status !== "running" && kick.normalized_text) {
        setNormalizedText(kick.normalized_text || ""); setShowTranscript(true); return true;
      }
      toast.info(`Reading the build sheet${kick.pages ? ` (${kick.pages} pages)` : ""} in the background — this can take a minute on a big sheet.`);
      // POLL the background job (no web-timeout ceiling) until done/error
      const deadline = Date.now() + 6 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: st } = await api.get(`/donors/${donorId}/ai/normalize-status`);
        if (st.status === "done") {
          setNormalizedText(st.normalized_text || "");
          setRawText(st.raw_text || "");
          setShowTranscript(true);
          setThinSheet(st.thin ? { reason: st.thin_reason, chars: st.chars, pages: st.pages, perPage: st.chars_per_page } : null);
          // A THIN SHEET IS NOT A SUCCESS — his MUST CLOSE, 2026-10-13. The green tick used to
          // fire on any non-empty transcript, so forty characters off a ten-page scan read exactly
          // like a clean read. The server now says whether the read is worth anything, and the
          // screen says it too: amber, in words, with the re-read right there.
          if (st.thin) toast.warning(st.thin_reason || "This sheet gave almost nothing — needs a human look.");
          else toast.success(`Build sheet normalized (${st.pages || "?"} page${st.pages === 1 ? "" : "s"}) — check it, then Extract`);
          return true;
        }
        if (st.status === "error") { toast.error(st.error || "Couldn't read the build sheet — try again."); return false; }
      }
      toast.error("Normalize is taking unusually long — check back in a moment.");
      return false;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
    finally { setNormalizing(false); }
  };

  // One-tap recovery for a flagged/failed sheet: re-read (normalize) then auto-extract.
  const reprocess = async () => {
    const ok = await normalize();
    if (ok) await extractFromSheet([]);
  };
  const extractFromSheet = async () => {
    const needsCategory = data?.category === "uncategorized" || data?.category_needs_review;
    setExtracting(true);
    try {
      const { data: kick } = await api.post(`/donors/${donorId}/ai/extract-from-sheet`, {
        refine: buildRefine(), suggest_category: needsCategory });
      // Extract now runs in the BACKGROUND (never killed by the web/proxy timeout on a big sheet).
      // If an older backend returned the result inline, honor it; otherwise POLL extract-status.
      if (kick.status !== "running" && (kick.filled !== undefined)) {
        setResult(kick);
        await loadSpecs();
        return;
      }
      toast.info("Extracting specs from the sheet in the background — this can take a minute on a big sheet.");
      const deadline = Date.now() + 6 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: st } = await api.get(`/donors/${donorId}/ai/extract-status`);
        if (st.status === "done") {
          const res = st.result || {};
          setResult(res);
          setReadReport(res);
          const bits = [`drafted ${res.filled}`];
          if (res.dedup?.length) bits.push(`${res.dedup.length} duplicate serial(s)!`);
          toast.success(`Extracted from sheet · ${bits.join(" · ")}`);
          await loadSpecs();
          return;
        }
        if (st.status === "error") { toast.error(st.error || "Couldn't extract specs from the sheet — try again."); return; }
      }
      toast.error("Extract is taking unusually long — check back in a moment.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setExtracting(false); }
  };

  const setCat = async (code) => {
    try { await setDonorCategory(donorId, code); await loadSpecs(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // HUMAN CORRECTION — add/remove a spec the AI missed, per component. Manual entries are kept even on AI re-runs.
  const addExtra = async () => {
    const dr = extraDraft._add || {};
    const component = dr.component || "general";
    if (!dr.label?.trim() || !dr.value?.trim()) { toast.error("Add a label and a value."); return; }
    try {
      const { data: res } = await api.post(`/donors/${donorId}/extra`, { component, label: dr.label.trim(), value: dr.value.trim() });
      setData(res); setExtraDraft((d) => ({ ...d, _add: { component } })); toast.success("Added");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeExtra = async (ex) => {
    try {
      const { data: res } = await api.delete(`/donors/${donorId}/extra`, { params: { component: ex.component || "general", label: ex.label, value: ex.value } });
      setData(res); toast.success("Removed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const regroup = async (kind, from_component, to_component, value, label = "") => {
    if (!to_component || to_component === from_component) return;
    try {
      const { data: res } = await api.post(`/donors/${donorId}/regroup`, { kind, from_component, to_component, value, label });
      setData(res); toast.success(`Moved to ${to_component.replace(/_/g, " ")}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const compOptions = (data?.components || []).map((c) => ({ code: c.code, label: c.label }));
  const saveVal = async (key, value) => {
    setBusyKey(key);
    try { const r = await api.put(`/donors/${donorId}/specs`, { values: { [key]: value } }); setData(r.data); setDraft((d) => { const n = { ...d }; delete n[key]; return n; }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusyKey(null); }
  };
  const confirm = async (key) => {
    setBusyKey(key);
    try { const r = await api.post(`/donors/${donorId}/specs/${key}/confirm`); setData(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusyKey(null); }
  };
  const applyHint = async (key, value) => saveVal(key, value);

  // EVERY AI VALUE GETS ITS OWN ✓ — there is no "confirm all" for them (his rule, 2026-10-20).
  const allFields = data ? data.groups.flatMap((g) => g.fields) : [];
  // B1 — the short "needs your eyes" list: unverified AND (low-confidence | sources disagree | duplicate | ambiguous serial)
  const needsEyes = allFields.filter((f) => f.unverified && f.value != null && f.value !== ""
    && (f.ai_confidence === "low" || f.ai_alt || f.dup_warning || (f.ai_ambiguous || []).length > 0));
  const reasonFor = (f) => {
    const r = [];
    if (f.ai_alt) r.push("sources disagree");
    if (f.dup_warning) r.push(`dup of ${f.dup_warning}`);
    if ((f.ai_ambiguous || []).length) r.push("hard-to-read chars");
    if (f.ai_confidence === "low") r.push("low confidence");
    return r;
  };
  const exportFindings = () => {
    if (!data) return;
    const lines = [`Truck ${donor?.stock_number || donorId} — AI spec draft`, ""];
    data.groups.forEach((g) => {
      lines.push(`[${g.group}]`);
      g.fields.forEach((f) => { if (f.value != null && f.value !== "") lines.push(`  ${f.label}: ${f.value}${f.unit ? " " + f.unit : ""}  (${f.source})`); });
    });
    navigator.clipboard.writeText(lines.join("\n")).then(() => toast.success("Findings copied")).catch(() => toast.error("Copy failed"));
  };

  // pull value + description out for their own cards
  const findField = (k) => allFields.find((f) => f.key === k);
  const valueField = findField("value");
  const descField = findField("description");
  const specGroups = data ? data.groups
    .map((g) => ({ ...g, fields: g.fields.filter((f) => f.key !== "value" && f.key !== "description") }))
    .filter((g) => g.fields.length) : [];

  const chip = (f) => {
    if (f.value == null || f.value === "") return null;
    if (f.source === "confirmed") return <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]"><Check className="w-3 h-3 mr-0.5" />Confirmed</Badge>;
    if (f.source === "manual") return <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]"><Check className="w-3 h-3 mr-0.5" />Entered</Badge>;
    const low = f.ai_confidence === "low";
    return <Badge className={`${low ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"} border text-[10px]`}><AlertTriangle className="w-3 h-3 mr-0.5" />{low ? "double-check · " : "AI · "}{f.ai_confidence || ""}</Badge>;
  };
  // A1 — provenance: where the value came from (Sheet / Photo / both agree / disagree).
  const sourceBadge = (f) => {
    if (f.value == null || f.value === "" || f.source === "manual" || f.source === "confirmed") return null;
    if (f.ai_agreement === "both") return <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]"><CheckCheck className="w-3 h-3 mr-0.5" />Sheet + Photo agree</Badge>;
    if (f.ai_agreement === "conflict" || f.ai_alt || f.ai_vin_conflict) return <Badge className="bg-red-50 text-red-700 border border-red-200 text-[10px]"><AlertTriangle className="w-3 h-3 mr-0.5" />sources disagree</Badge>;
    if (f.ai_source === "build_sheet") return <Badge className="bg-slate-100 text-slate-600 border border-slate-200 text-[10px]"><FileText className="w-3 h-3 mr-0.5" />Sheet</Badge>;
    if (f.ai_source === "photo") return <Badge className="bg-sky-50 text-sky-700 border border-sky-200 text-[10px]"><Camera className="w-3 h-3 mr-0.5" />Photo</Badge>;
    return null;
  };
  // A1 conflict + A2 ambiguous-serial verify, shown under a field row.
  const provenanceRow = (f) => {
    const showConflict = f.ai_alt && f.value != null && f.value !== "";
    const vinClash = f.ai_vin_conflict && f.value != null && f.value !== "";
    const amb = f.ai_ambiguous || [];
    if (!showConflict && !vinClash && amb.length === 0 && !f.ai_photo_ref) return null;
    return (
      <div className="mt-1 space-y-1">
        {showConflict && (
          <div data-testid={`wb-conflict-${f.key}`} className="flex flex-wrap items-center gap-1.5 text-[11px] rounded-md bg-red-50 border border-red-200 px-2 py-1">
            <AlertTriangle className="w-3 h-3 text-red-600 shrink-0" />
            <span className="text-red-700">{f.ai_source === "build_sheet" ? "Sheet" : "Photo"}: <b>{String(f.value)}</b></span>
            <span className="text-slate-400">vs</span>
            <span className="text-red-700">{f.ai_alt.source === "build_sheet" ? "Sheet" : "Photo"}: <b>{f.ai_alt.value}</b></span>
            <button onClick={() => saveVal(f.key, f.ai_alt.value)} className="ml-auto text-[10px] underline text-red-700">use {f.ai_alt.value}</button>
          </div>
        )}
        {/* HIS ADDITION, 2026-10-09: the VIN decode checked against what the AI read off the
            sheet and the photos. Same red flag, nothing changed automatically. */}
        {vinClash && (
          <div data-testid={`wb-vin-conflict-${f.key}`} className="flex flex-wrap items-center gap-1.5 text-[11px] rounded-md bg-red-50 border border-red-200 px-2 py-1">
            <AlertTriangle className="w-3 h-3 text-red-600 shrink-0" />
            <span className="text-red-700">{f.ai_source === "build_sheet" ? "Sheet" : "Photo"}: <b>{String(f.value)}</b></span>
            <span className="text-slate-400">vs</span>
            <span className="text-red-700">VIN decode: <b>{f.ai_vin_conflict.value}</b></span>
            <button onClick={() => saveVal(f.key, f.ai_vin_conflict.value)} className="ml-auto text-[10px] underline text-red-700">use {f.ai_vin_conflict.value}</button>
          </div>
        )}
        {amb.length > 0 && (
          <div data-testid={`wb-ambiguous-${f.key}`} className="flex flex-wrap items-center gap-1.5 text-[11px] rounded-md bg-amber-50 border border-amber-200 px-2 py-1">
            <Eye className="w-3 h-3 text-amber-600 shrink-0" />
            <span className="text-amber-800">Hard-to-read: <b className="font-mono">{amb.join(" ")}</b> — verify before trusting this serial</span>
            {f.ai_photo_ref && <button onClick={() => setPhotoPreview({ fileId: f.ai_photo_ref, label: f.label })} className="ml-auto text-[10px] underline text-amber-800 inline-flex items-center gap-0.5"><Camera className="w-3 h-3" />view photo</button>}
          </div>
        )}
        {!amb.length && !showConflict && f.ai_photo_ref && (
          <button onClick={() => setPhotoPreview({ fileId: f.ai_photo_ref, label: f.label })} className="text-[10px] underline text-sky-700 inline-flex items-center gap-0.5"><Camera className="w-3 h-3" />see the photo this came from</button>
        )}
      </div>
    );
  };
  const renderInput = (f) => {
    const cur = draft[f.key] !== undefined ? draft[f.key] : (f.value ?? "");
    const set = (v) => setDraft((d) => ({ ...d, [f.key]: v }));
    if (f.type === "bool") {
      const v = cur === true || cur === "true";
      return (
        <div className="flex gap-1">
          <button data-testid={`wb-val-${f.key}-yes`} onClick={() => set(true)} className={`px-3 py-1 rounded-lg text-xs border ${v ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500"}`}>Yes</button>
          <button data-testid={`wb-val-${f.key}-no`} onClick={() => set(false)} className={`px-3 py-1 rounded-lg text-xs border ${cur === false ? "border-slate-400 bg-slate-100" : "border-slate-200 text-slate-500"}`}>No</button>
        </div>
      );
    }
    if (f.type === "enum") {
      return (
        <Select value={cur ? String(cur) : ""} onValueChange={set}>
          <SelectTrigger data-testid={`wb-val-${f.key}`} className="rounded-lg h-8 w-44 text-sm"><SelectValue placeholder="Choose…" /></SelectTrigger>
          <SelectContent>{f.enum_options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    const sugg = (f.enum_options || []).filter((o) => !o.archived);
    const listId = sugg.length ? `dl-${f.key}` : undefined;
    return (
      <>
        <Input data-testid={`wb-val-${f.key}`} value={cur} onChange={(e) => set(e.target.value)} list={listId}
          type={f.type === "number" ? "number" : "text"} placeholder={f.unit || (sugg.length ? "type or pick…" : "—")} className="rounded-lg h-8 w-40 text-sm" />
        {listId && <datalist id={listId}>{sugg.map((o) => <option key={o.value} value={o.label} />)}</datalist>}
      </>
    );
  };
  const dirty = (f) => draft[f.key] !== undefined && String(draft[f.key]) !== String(f.value ?? "");

  const activeCats = categories.filter((c) => !c.archived);
  const needsCat = data && (data.category === "uncategorized" || data.category_needs_review);

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !scanning && onOpenChange(o)}>
        <DialogContent data-testid="truck-ai-workbench" className="max-w-[96vw] w-[96vw] h-kb max-h-kb p-0 rounded-xl overflow-hidden gap-0 flex flex-col">
          <DialogTitle className="sr-only">Ask AI — truck</DialogTitle>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b bg-gradient-to-r from-[#B01E28] to-[#E0781A] text-white shrink-0">
            <Sparkles className="w-5 h-5" />
            <span className="font-bold">Ask AI · {donor?.stock_number || "truck"}</span>
            <span className="text-white/70 text-xs hidden md:inline">reads only the photos you pick + the on-screen context — nothing else</span>
            <span className="flex-1" />
            {isAdmin && result?.cost && <Badge className="bg-black/25 text-white border-0 text-[11px] mr-1">${result.cost.actual_usd?.toFixed?.(4)} · {result.cost.input_tokens + result.cost.output_tokens} tok</Badge>}
            <button data-testid="wb-close" onClick={() => onOpenChange(false)} className="p-1.5 rounded-lg hover:bg-white/15"><X className="w-5 h-5" /></button>
          </div>

          <PreflightBanner active={open} />

          <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
            {/* LEFT — photo stage: dedicated full-height dark column (identical model to the parts Ask AI) */}
            <div className="lg:w-3/5 bg-slate-900 flex flex-col min-h-0">
              {photos.length === 0 ? (
                <div data-testid="wb-no-photos" className="flex-1 flex flex-col items-center justify-center gap-2 text-slate-400">
                  <ImageOff className="w-8 h-8" /><span className="text-sm px-6 text-center">No photos on this truck — you can still scan the build sheet from the panel on the right.</span>
                </div>
              ) : (() => {
                const idx = Math.min(active, photos.length - 1);
                const cur = photos[idx];
                const done = readIds.has(cur.file_id);
                return (
                <>
                  <div data-testid="wb-photo-main" className="relative flex-1 min-h-0 overflow-hidden">
                    <PhotoStage fileId={cur.file_id} alt={`photo ${idx + 1}`} gestures={photoG}
                      controls={false} testidPrefix="wb-photo" className="p-3" />
                    <div className="absolute top-4 right-4 flex gap-1.5 items-center">
                      <PhotoControls g={photoG} testidPrefix="wb-photo" />
                      <button data-testid="wb-photo-full" onClick={(e) => { e.stopPropagation(); setFullIdx(idx); }}
                        title="Full screen" className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/90 text-slate-700 hover:bg-white shadow border border-slate-200/60">
                        <Maximize2 className="w-5 h-5" />
                      </button>
                    </div>
                    <span data-testid="wb-stage-read-state"
                      className={`absolute top-4 left-4 rounded-full px-3 py-1.5 text-xs font-semibold ${done ? "bg-emerald-600 text-white" : "bg-black/50 text-white"}`}>
                      {done ? <><Check className="w-3.5 h-3.5 mr-1 inline" />Read by AI</> : "Not read yet"}
                    </span>
                    {photos.length > 1 && (
                      <>
                        <button data-testid="wb-photo-prev" onClick={(e) => { e.stopPropagation(); stepPhoto(-1); }} className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2"><ChevronLeft className="w-5 h-5" /></button>
                        <button data-testid="wb-photo-next" onClick={(e) => { e.stopPropagation(); stepPhoto(1); }} className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2"><ChevronRight className="w-5 h-5" /></button>
                      </>
                    )}
                    <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[11px] text-white/80 bg-black/50 rounded-full px-2 py-0.5">{idx + 1} / {photos.length}{done ? " · read" : ""} · {photoG.zoomed ? "drag to move" : "tap or pinch to zoom"}</span>
                  </div>
                  <div className="flex gap-2 overflow-x-auto p-2 border-t border-white/10 shrink-0">
                    {photos.map((p, i) => {
                      const sc = readIds.has(p.file_id);
                      const act = idx === i;
                      return (
                        <button key={p.file_id} data-testid={`wb-photo-${p.file_id}`} onClick={() => { setActive(i); resetView(); }}
                          className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 shrink-0 ${sc ? "border-emerald-500" : "border-transparent opacity-60 hover:opacity-100"} ${act ? "ring-2 ring-white/70" : ""}`}>
                          <AuthImg fileId={p.file_id} thumb className="w-full h-full object-cover" alt={`thumb ${i + 1}`} />
                          {sc && <span className="absolute bottom-0 inset-x-0 bg-emerald-600/85 text-white text-[8px] text-center leading-tight">read</span>}
                        </button>
                      );
                    })}
                  </div>
                </>
                );
              })()}
            </div>

            {/* RIGHT — controls + review in ONE scroll (parts-parity split) */}
            <div className="lg:w-2/5 flex flex-col min-h-0 border-t lg:border-t-0 lg:border-l bg-slate-50/50">
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* WHAT IS LEFT TO READ — the honest count, from the server's own stamps. */}
                <div className="flex items-center gap-2 flex-wrap" data-testid="wb-photo-read-summary">
                  <Camera className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-bold text-slate-700">Photos</span>
                  <span className="text-xs text-slate-500">
                    {photos.length === 0 ? "none on this truck"
                      : unreadCount === 0 ? `all ${photos.length} read`
                        : `${readIds.size} of ${photos.length} read · ${unreadCount} still to read`}
                  </span>
                </div>
                {hasBuildSheet && (
                  <label className="flex items-center justify-between mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                    <span className="flex items-center gap-1.5"><FileText className="w-4 h-4 text-slate-500" />Include the build sheet</span>
                    <Switch data-testid="wb-buildsheet" checked={includeBuildSheet} onCheckedChange={setIncludeBuildSheet} disabled={scanning} />
                  </label>
                )}

                {/* TWO-STEP BUILD SHEET — Normalize (transcribe any format) → Extract (from clean text). */}
                {hasBuildSheet && (
                  <div data-testid="wb-buildsheet-twostep" className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3 space-y-2.5">
                    <div className="flex items-center gap-1.5">
                      <ScanText className="w-4 h-4 text-indigo-600" />
                      <span className="text-sm font-bold text-indigo-900">Build sheet — read in two steps</span>
                      <span className="text-[10px] text-indigo-500">most reliable for long / faxed / any-format sheets</span>
                    </div>
                    <p className="text-[11px] text-indigo-700/80 leading-snug">
                      <b>1. Normalize</b> transcribes the sheet (any format) into one clean, faithful copy you can check — it invents nothing and looks nothing up. <b>2. Extract</b> reads that clean copy to fill your fields + group serials. Re-extract is cheap and never re-reads the sheet.
                    </p>

                    {!normalizedText ? (
                      <div className="space-y-2">
                        <Button data-testid="wb-normalize" onClick={normalize} disabled={normalizing || scanning}
                          className="w-full h-10 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white">
                          {normalizing ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Reading & tidying the sheet…</> : <><ScanText className="w-4 h-4 mr-2" />1 · Normalize build sheet</>}
                        </Button>
                        <Button data-testid="wb-reprocess" variant="outline" onClick={reprocess} disabled={normalizing || extracting || scanning}
                          className="w-full h-9 rounded-xl border-indigo-200 text-indigo-700">
                          <RotateCw className="w-3.5 h-3.5 mr-1.5" />Re-read sheet (normalize + extract)
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 flex-wrap">
                          {thinSheet
                            ? <Badge data-testid="wb-thin-badge" className="bg-amber-50 text-[#8a4a09] border border-[#E0781A] text-[10px]"><AlertTriangle className="w-3 h-3 mr-0.5" />Needs a human look</Badge>
                            : landedNothing
                            ? <Badge data-testid="wb-landed-badge" className="bg-amber-50 text-[#8a4a09] border border-[#E0781A] text-[10px]"><AlertTriangle className="w-3 h-3 mr-0.5" />Nothing landed</Badge>
                            : <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]"><Check className="w-3 h-3 mr-0.5" />Normalized</Badge>}
                          <button data-testid="wb-transcript-toggle" onClick={() => setShowTranscript((s) => !s)}
                            className="text-[11px] text-indigo-700 underline underline-offset-2 hover:text-indigo-900">
                            {showTranscript ? "Hide" : "View"} normalized sheet
                          </button>
                          <button data-testid="wb-compare" onClick={() => setShowCompare(true)}
                            className="text-[11px] text-indigo-700 underline underline-offset-2 hover:text-indigo-900 inline-flex items-center gap-0.5">
                            <Columns2 className="w-3 h-3" />Compare ↔ original
                          </button>
                          {rawText && (
                            <button data-testid="wb-rawtext-toggle" onClick={() => setShowRaw((s) => !s)}
                              className="text-[11px] text-slate-600 underline underline-offset-2 hover:text-slate-900">
                              {showRaw ? "Hide" : "View"} everything read (raw)
                            </button>
                          )}
                          <span className="flex-1" />
                          <button data-testid="wb-renormalize" onClick={normalize} disabled={normalizing || scanning}
                            className="text-[11px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
                            {normalizing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}Re-normalize
                          </button>
                        </div>
                        {/* THE SHEET SAID ALMOST NOTHING — say it where the work happens, in words,
                            with both ways out. His MUST CLOSE, 2026-10-13: *"A THIN BUILD SHEET
                            READING AS SUCCESS."* Nothing is hidden: the transcript is still there
                            to open, and Extract is still allowed — a person decides, not the screen. */}
                        {thinSheet && (
                          <div data-testid="wb-thin-panel" className="rounded-lg border border-[#E0781A] bg-amber-50 px-3 py-2 text-[11.5px] text-[#8a4a09] space-y-1.5">
                            <div className="font-bold flex items-start gap-1.5">
                              <AlertTriangle className="w-4 h-4 shrink-0 mt-[1px] text-[#B01E28]" />
                              <span>{thinSheet.reason}</span>
                            </div>
                            <div className="text-[#8a4a09]/90">
                              A real build-sheet page carries hundreds of characters. This one came back with
                              {" "}<b className="font-mono">{thinSheet.perPage?.toLocaleString?.() ?? thinSheet.perPage}</b> a page —
                              usually a smeared scan, a sideways page, or a photo of a screen. Check the transcript
                              against the sheet before you trust anything it filled in.
                            </div>
                            <div className="flex items-center gap-2 flex-wrap pt-0.5">
                              <Button data-testid="wb-thin-reread" size="sm" onClick={normalize} disabled={normalizing || scanning}
                                className="h-8 rounded-lg bg-[#B01E28] hover:bg-[#8f1820] text-white text-[11px]">
                                {normalizing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RotateCw className="w-3.5 h-3.5 mr-1" />}Re-read the sheet
                              </Button>
                              <button data-testid="wb-thin-transcript" onClick={() => setShowTranscript(true)}
                                className="text-[11px] underline underline-offset-2 hover:text-[#B01E28]">Open the transcript</button>
                            </div>
                          </div>
                        )}
                        {/* THE READ WAS FINE AND NOTHING LANDED — the other half of the same lie,
                            and the half production actually has. A re-read fixes nothing here: the
                            transcript is good, it was never EXTRACTED onto the truck. So the words
                            and the button are different. */}
                        {!thinSheet && landedNothing && (
                          <div data-testid="wb-landed-panel" className="rounded-lg border border-[#E0781A] bg-amber-50 px-3 py-2 text-[11.5px] text-[#8a4a09] space-y-1.5">
                            <div className="font-bold flex items-start gap-1.5">
                              <AlertTriangle className="w-4 h-4 shrink-0 mt-[1px] text-[#B01E28]" />
                              <span>{landedNothing.reason}</span>
                            </div>
                            <div className="text-[#8a4a09]/90">
                              The sheet itself read fine — this is not a re-read job. Press
                              {" "}<b>2 · Extract</b> below to pull the specs off the transcript and onto the truck.
                            </div>
                          </div>
                        )}
                        {showTranscript && (
                          <pre data-testid="wb-transcript" className="max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white p-2 text-[10.5px] leading-snug font-mono text-slate-700 whitespace-pre-wrap">{normalizedText}</pre>
                        )}
                        {showRaw && rawText && (
                          <div data-testid="wb-rawtext-wrap">
                            <p className="text-[10px] text-slate-500 mb-1">This is the <b>complete</b> text the AI read from the sheet{rawText.length ? ` (${rawText.length.toLocaleString()} chars)` : ""}. The normalized transcript above keeps the real specs and drops only pure layout / BOM / drawing-code noise — skim this to confirm nothing valuable was left out.</p>
                            <pre data-testid="wb-rawtext" className="max-h-56 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-2 text-[10px] leading-snug font-mono text-slate-600 whitespace-pre-wrap">{rawText}</pre>
                          </div>
                        )}
                        <p className="text-[10px] text-indigo-500/90">Check it against the original — every serial should match exactly. Then extract.</p>
                        <div className="flex flex-col gap-2">
                          <Button data-testid="wb-extract" onClick={extractFromSheet} disabled={extracting || scanning}
                            className="w-full h-10 rounded-xl bg-gradient-to-r from-[#B01E28] to-[#E0781A] text-white hover:opacity-90">
                            {extracting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Extracting…</> : <><Sparkles className="w-4 h-4 mr-2" />2 · Extract specs from sheet</>}
                          </Button>
                          <span className="text-[10px] text-slate-400 text-center">Extract from sheet is cheap — re-run it anytime without re-reading the sheet.</span>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* staff hint + voice */}
              <div>
                <div className="text-sm font-bold text-slate-700 mb-1.5">Add what you know (steers the AI, never becomes a number)</div>
                <div className="relative">
                  <Textarea data-testid="wb-hint" value={hint} onChange={(e) => setHint(e.target.value)} rows={2}
                    placeholder="e.g. focus on the engine dataplate; trans is an Eaton 10-speed" className="rounded-lg text-sm pr-10" />
                  <button data-testid="wb-mic" onClick={toggleMic} title="Dictate" className={`absolute right-2 top-2 p-1.5 rounded-lg ${listening ? "bg-red-600 text-white animate-pulse" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                    {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* transparency: everything the AI gets */}
              <div data-testid="wb-transparency" className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-600 mb-2"><Info className="w-4 h-4 text-slate-400" />Sent to the AI</div>
                <div className="flex flex-wrap gap-1.5">
                  {donor?.vin && <Badge className="bg-slate-100 text-slate-600 text-[10px]">VIN {donor.vin}</Badge>}
                  {[["Year", donor?.year], ["Make", donor?.make], ["Model", donor?.model], ["Engine", donor?.engine_model]].map(([l, v]) => v ? <Badge key={l} className="bg-slate-100 text-slate-600 text-[10px]">{l}: {v}</Badge> : null)}
                  {data && <Badge className="bg-indigo-50 text-indigo-700 text-[10px]">category: {categories.find((c) => c.code === data.category)?.name || data.category}</Badge>}
                  {vinDecode && <Badge className="bg-sky-50 text-sky-700 border border-sky-200 text-[10px]">VIN decodes → {[vinDecode.year, vinDecode.make, vinDecode.model].filter(Boolean).join(" ")}</Badge>}
                  {hint.trim() && <Badge className="bg-amber-50 text-amber-700 text-[10px]">hint: {hint.trim().slice(0, 40)}</Badge>}
                  <Badge className="bg-slate-100 text-slate-500 text-[10px]">{unreadCount} photo(s) still to read{includeBuildSheet && hasBuildSheet ? " + build sheet" : ""}</Badge>
                </div>
                <p className="text-[10px] text-slate-400 mt-2">The AI fills from your spec registry (preset fields, enum-locked). It never invents a serial or number.</p>
              </div>

              {needsCat ? (
                <div className="space-y-2">
                  <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-3">
                    <div className="flex items-center gap-2 mb-1.5"><AlertTriangle className="w-4 h-4 text-amber-600" /><span className="text-sm font-semibold text-amber-900">Categorize this truck first</span></div>
                    <p className="text-xs text-amber-800 mb-2">A full spec scan can't populate anything until the truck has a category — it's blocked so you never spend on a scan that can't produce a result. Pick a category, or let AI suggest one from a single photo (cheap).</p>
                    <Select value={data.category} onValueChange={setCat}>
                      <SelectTrigger data-testid="wb-category" className="h-9 w-full rounded-lg text-xs"><SelectValue placeholder="Choose category…" /></SelectTrigger>
                      <SelectContent>{activeCats.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="wb-suggest-category" onClick={startScan} disabled={scanning} variant="outline" className="w-full h-11 rounded-xl border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                    {scanning ? <><Loader2 className="w-5 h-5 animate-spin mr-2" />Looking…</> : <><Wand2 className="w-5 h-5 mr-2" />Suggest category (AI · 1 photo)</>}
                  </Button>
                  {scan?.category_suggestion && (
                    <div data-testid="wb-cat-suggestion" className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
                      <Wand2 className="w-4 h-4" />AI suggests <b>{scan.category_suggestion.name}</b> ({scan.category_suggestion.confidence})
                      <Button size="sm" onClick={() => setCat(scan.category_suggestion.code)} className="ml-auto h-7 rounded-lg bg-indigo-600 text-xs">Use</Button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* ONE BUTTON, ONE WAY. No hidden "more than 20 photos" rule deciding which
                      version of the scan somebody gets — every truck reads the same way, and it
                      only reads what has never been read. */}
                  <Button data-testid="wb-scan" onClick={startScan} disabled={scanning || (unreadCount === 0 && !(includeBuildSheet && hasBuildSheet))}
                    className="w-full h-12 rounded-xl bg-gradient-to-r from-[#B01E28] to-[#E0781A] text-white hover:opacity-90 text-base">
                    {scanning
                      ? <><Loader2 className="w-5 h-5 animate-spin mr-2" />Reading…</>
                      : <><Sparkles className="w-5 h-5 mr-2" />
                        {unreadCount === 0
                          ? (hasBuildSheet && includeBuildSheet ? "Read the build sheet" : "Every photo has been read")
                          : `Read ${unreadCount} photo${unreadCount === 1 ? "" : "s"}${hasBuildSheet && includeBuildSheet ? " + the build sheet" : ""}`}
                      </>}
                  </Button>
                  {scan && scan.status !== "idle" && (
                    <div data-testid="wb-scan-progress"
                      className={`rounded-lg border px-3 py-2 ${scan.status === "running" ? "border-[#E0781A]/30 bg-orange-50"
                        : scan.status === "done" ? "border-emerald-300 bg-emerald-50" : "border-amber-400 bg-amber-50"}`}>
                      <div className="flex items-center justify-between text-xs font-semibold mb-1">
                        <span className={scan.status === "running" ? "text-[#C76714]" : scan.status === "done" ? "text-emerald-800" : "text-amber-900"}>
                          {scan.status === "running" && <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1" />}
                          {scan.message || (scan.status === "running" ? "Reading…" : scan.status)}
                          {isAdmin && scan.cost ? ` · $${Number(scan.cost).toFixed(4)}` : ""}
                        </span>
                        {scan.total > 0 && <span className="text-slate-500">{scan.read}/{scan.total}</span>}
                      </div>
                      {scan.total > 0 && (
                        <div className="h-2 rounded-full bg-orange-100 overflow-hidden">
                          <div className={`h-full transition-all ${scan.status === "done" ? "bg-emerald-500" : "bg-[#E0781A]"}`}
                            style={{ width: `${Math.round((scan.read / Math.max(1, scan.total)) * 100)}%` }} />
                        </div>
                      )}
                      {scan.status === "running" && (
                        <p className="text-[10px] text-slate-500 mt-1">Walk to the next truck — this keeps going on the server. Close this and come back any time.</p>
                      )}
                      {(scan.status === "stopped" || scan.status === "error") && (
                        <Button data-testid="wb-scan-restart" size="sm" variant="outline" onClick={startScan} disabled={kicking}
                          className="mt-2 h-8 rounded-lg border-amber-500 text-amber-900 text-xs">
                          <RotateCw className="w-3.5 h-3.5 mr-1.5" />Start again — finish the remaining {scan.unread} photo{scan.unread === 1 ? "" : "s"}
                        </Button>
                      )}
                    </div>
                  )}
                </>
              )}


              {/* dup-serial catch (inline) */}
              {result?.dedup?.length > 0 && (
                <div data-testid="wb-dup-catch" className="rounded-lg border-2 border-red-300 bg-red-50 p-3 space-y-1">
                  <div className="flex items-center gap-1.5 text-sm font-bold text-red-700"><AlertTriangle className="w-4 h-4" />Duplicate serial — verify before you spawn a part</div>
                  {result.dedup.map((d) => <div key={d.key} className="text-xs text-red-700">{d.label} <b>{d.value}</b> is already on <b>{d.other_stock}</b></div>)}
                </div>
              )}

              {/* COMPONENTS — AI groups each tag's numbers under its component; spawn the whole thing → a part */}
              {canParts && (data?.spawnable_components || []).length > 0 && (
                <div data-testid="wb-components" className="rounded-lg border border-slate-300 bg-white p-3">
                  <div className="flex items-center gap-1.5 mb-2"><Boxes className="w-4 h-4 text-slate-500" /><span className="text-sm font-bold text-slate-700">Components</span><span className="text-[10px] text-slate-400">AI groups each tag's numbers by component — spawn the whole thing into a part (numbers move with it)</span></div>
                  <div className="space-y-2">
                    {data.spawnable_components.map((c) => {
                      const comp = c.code, label = c.label;
                      const nums = (data.component_numbers?.[comp] || []);
                      const groupVals = allFields.filter((f) => f.key.startsWith(comp + "_") && f.key !== "value" && f.key !== "description" && f.value != null && f.value !== "");
                      const hasData = nums.length > 0 || groupVals.some((f) => f.key.endsWith("_serial"));
                      return (
                        <div key={comp} data-testid={`wb-component-${comp}`} className="rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-700">{label}</span>
                            <span className="flex-1" />
                            <Button data-testid={`wb-spawn-${comp}`} size="sm" disabled={!hasData} onClick={() => setCpt({ component: comp, suggestedName: label, componentNumbers: [
                              ...groupVals.map((f) => ({ label: f.label, value: String(f.value) })),
                              ...nums.map((n) => ({ label: n.label, value: n.value })),
                            ] })} className="h-7 rounded-lg bg-[#B01E28] hover:bg-[#8B1820] text-white text-[11px]"><PackagePlus className="w-3 h-3 mr-1" />Make this {label} a part</Button>
                          </div>
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {groupVals.map((f) => (
                              <span key={f.key} className="text-[11px] font-mono bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-600">{f.label}: {String(f.value)}</span>
                            ))}
                            {nums.map((n, i) => (
                              <span key={`n${i}`} data-testid={`wb-compnum-${comp}-${i}`} className="group text-[11px] font-mono bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 text-amber-800 inline-flex items-center gap-1">
                                {n.label}: {n.value}
                                <AiValueTick donorId={donorId} kind="number" component={comp} item={n} canEdit={data.can_edit} onDone={loadSpecs} testid={`wb-compnum-${comp}-${i}`} />
                                {data.can_edit && compOptions.length > 1 && (
                                  <select data-testid={`wb-move-compnum-${comp}-${i}`} title="Move to the right component" value=""
                                    onChange={(ev) => regroup("number", comp, ev.target.value, n.value, n.label)}
                                    className="opacity-40 group-hover:opacity-100 bg-transparent text-[10px] border-0 cursor-pointer text-amber-700">
                                    <option value="">move…</option>
                                    {compOptions.filter((o) => o.code !== comp).map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
                                  </select>
                                )}
                              </span>
                            ))}
                            {!hasData && <span className="text-[11px] text-slate-400 italic">no numbers read yet — scan this component's tag</span>}
                          </div>
                          {/* one-tap AI-grouping feedback (live-vehicle validation; advisory read-out is owner/admin-only) */}
                          {hasData && (
                            <div data-testid={`wb-group-feedback-${comp}`} className="flex items-center gap-2 mt-2 pt-1.5 border-t border-slate-200/70">
                              {groupFb[comp] ? (
                                <span data-testid={`wb-group-feedback-done-${comp}`} className={`text-[11px] font-semibold flex items-center gap-1 ${groupFb[comp] === "correct" ? "text-emerald-600" : "text-red-600"}`}>
                                  {groupFb[comp] === "correct" ? <ThumbsUp className="w-3 h-3" /> : <ThumbsDown className="w-3 h-3" />}
                                  {groupFb[comp] === "correct" ? "Marked grouped right" : "Flagged — grouped wrong"}
                                </span>
                              ) : (
                                <>
                                  <span className="text-[11px] text-slate-400">Did the AI group these numbers right?</span>
                                  <span className="flex-1" />
                                  <Button data-testid={`wb-group-right-${comp}`} size="sm" variant="outline"
                                    onClick={() => sendGroupFeedback(comp, "correct", [...groupVals.map((f) => ({ label: f.label, value: String(f.value) })), ...nums.map((n) => ({ label: n.label, value: n.value }))])}
                                    className="h-6 rounded-md border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-[11px] px-2"><ThumbsUp className="w-3 h-3 mr-1" />Right</Button>
                                  <Button data-testid={`wb-group-wrong-${comp}`} size="sm" variant="outline"
                                    onClick={() => sendGroupFeedback(comp, "wrong", [...groupVals.map((f) => ({ label: f.label, value: String(f.value) })), ...nums.map((n) => ({ label: n.label, value: n.value }))])}
                                    className="h-6 rounded-md border-red-200 text-red-700 hover:bg-red-50 text-[11px] px-2"><ThumbsDown className="w-3 h-3 mr-1" />Wrong</Button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* VALUE (cost-walled) */}
              {showCost && valueField && (
                <div data-testid="wb-value-card" className="rounded-lg border border-slate-300 bg-white p-3">
                  <div className="flex items-center gap-1.5 mb-1"><DollarSign className="w-4 h-4 text-slate-500" /><span className="text-sm font-bold text-slate-700">Estimated value</span>{valueField.unverified && valueField.value != null && <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px] ml-1">AI Assist · ESTIMATE · unverified</Badge>}</div>
                  {result?.ballpark_value && <div className="text-xs text-slate-500 mb-1.5">AI ballpark: {money(result.ballpark_value.low)} – {money(result.ballpark_value.high)} · rec {money(result.ballpark_value.recommended)} ({result.ballpark_value.confidence}){result.ballpark_value.note ? ` · ${result.ballpark_value.note}` : ""}</div>}
                  <div className="flex items-center gap-2">
                    {renderInput(valueField)}
                    {dirty(valueField) && <Button size="sm" onClick={() => saveVal(valueField.key, draft[valueField.key])} disabled={busyKey === valueField.key} className="h-8 rounded-lg bg-[#E0781A] text-xs">Save</Button>}
                    {valueField.value != null && valueField.value !== "" && valueField.unverified && <Button data-testid="wb-value-confirm" size="sm" variant="outline" onClick={() => confirm(valueField.key)} disabled={busyKey === valueField.key} className="h-8 rounded-lg border-emerald-300 text-emerald-700 text-xs"><Check className="w-3.5 h-3.5 mr-1" />Confirm</Button>}
                  </div>
                </div>
              )}

              {/* DESCRIPTION (internal) */}
              {descField && (
                <div data-testid="wb-desc-card" className="rounded-lg border border-slate-300 bg-white p-3">
                  <div className="flex items-center gap-1.5 mb-1"><FileText className="w-4 h-4 text-slate-500" /><span className="text-sm font-bold text-slate-700">Description</span><span className="text-[10px] text-slate-400">internal — never auto-crosses to public</span>{descField.unverified && descField.value && <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px] ml-1">AI · unverified</Badge>}</div>
                  <Textarea data-testid="wb-desc-input" rows={3} value={draft.description !== undefined ? draft.description : (descField.value ?? "")} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} className="rounded-lg text-sm" placeholder="AI-drafted summary appears here after a scan…" />
                  <div className="flex items-center gap-2 mt-1.5">
                    {dirty(descField) && <Button size="sm" onClick={() => saveVal("description", draft.description)} disabled={busyKey === "description"} className="h-8 rounded-lg bg-[#E0781A] text-xs">Save</Button>}
                    {descField.value && descField.unverified && <Button data-testid="wb-desc-confirm" size="sm" variant="outline" onClick={() => confirm("description")} className="h-8 rounded-lg border-emerald-300 text-emerald-700 text-xs"><Check className="w-3.5 h-3.5 mr-1" />Confirm</Button>}
                    {descField.value && <Button size="sm" variant="ghost" onClick={() => navigator.clipboard.writeText(descField.value).then(() => toast.success("Copied"))} className="h-8 rounded-lg text-xs text-slate-500"><Copy className="w-3.5 h-3.5 mr-1" />Copy</Button>}
                  </div>
                </div>
              )}

              {/* confirm bar + export */}
              <div className="flex flex-wrap items-center gap-2">
                <span data-testid="wb-one-at-a-time" className="text-[11px] text-slate-500">Each AI value needs its own ✓ — tap ✓verify (or Confirm) on each one you have checked.</span>
                <span className="flex-1" />
                <Button data-testid="wb-export" size="sm" variant="ghost" onClick={exportFindings} className="h-8 rounded-lg text-xs text-slate-500"><Copy className="w-3.5 h-3.5 mr-1" />Export findings</Button>
              </div>

              {/* scan summary + gap re-scan */}
              {result && (
                <div data-testid="wb-summary" className={`rounded-lg border px-3 py-2 text-xs space-y-1 ${thinResult ? "border-[#E0781A] bg-amber-50 text-[#8a4a09]" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
                  <div><Sparkles className={`w-3.5 h-3.5 inline mr-1 ${thinResult ? "text-[#B01E28]" : "text-[#E0781A]"}`} />Drafted <b>{result.filled}</b> from {result.images_used?.photos || 0} photo(s){result.images_used?.build_sheet ? " + build sheet" : ""}.</div>
                  {/* A SHEET THAT FILLED ALMOST NOTHING IS NOT A SUCCESS — his MUST CLOSE,
                      2026-10-13. This line used to print "Drafted 1" in the same neutral grey as a
                      full read, so nothing on the screen said a person should go and look. */}
                  {thinResult && (
                    <div data-testid="wb-summary-thin" className="font-bold">
                      {result.thin_result_reason || "That is almost nothing off a build sheet — needs a human look."}
                      {" "}Re-read it, or open the transcript and check what the sheet actually says.
                    </div>
                  )}
                  {result.missing?.length > 0 && (
                    <div className={thinResult ? "text-[#8a4a09]" : "text-slate-500"}>Still missing: {result.missing.map((m) => m.label).join(", ")}. <span className="text-[#C76714]">Point the camera at those tags, add them, then Scan the next batch.</span></div>
                  )}
                  {result.unreadable_note && <div className="text-slate-400 italic">{result.unreadable_note}</div>}
                </div>
              )}

              <AiReadReport report={readReport} />

              {/* recommended parts the AI can see on this truck (advisory — spawn on demand) */}
              {result?.recommended_parts?.length > 0 && (
                <div data-testid="wb-recommended-parts" className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-violet-600 mb-1.5 flex items-center gap-1"><Boxes className="w-3.5 h-3.5" />Sellable components AI sees here</div>
                  <div className="space-y-1">
                    {result.recommended_parts.map((r, i) => (
                      <div key={i} data-testid={`wb-rec-part-${r.component}`} className="text-xs text-violet-900"><b className="capitalize">{(r.component || "").replace(/_/g, " ")}</b>{r.reason ? <span className="text-violet-700"> — {r.reason}</span> : ""}</div>
                    ))}
                  </div>
                  <p className="text-[10px] text-violet-500 mt-1.5">Advisory only — nothing is created. Branch a part on demand from its component block.</p>
                </div>
              )}

              {/* ALL numbers the AI read, grouped by component (parts-parity: show everything it saw) */}
              {result && Object.keys(result.component_numbers || {}).length > 0 && (
                <div data-testid="wb-all-numbers" className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-amber-700 mb-1.5">Numbers read — grouped by component</div>
                  <div className="space-y-1.5">
                    {Object.entries(result.component_numbers).map(([code, nums]) => (
                      (nums || []).length > 0 && (
                        <div key={code} data-testid={`wb-numbers-${code}`}>
                          <span className="text-[11px] font-semibold text-slate-700 capitalize">{code.replace(/_/g, " ")}</span>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {nums.map((n, i) => (<span key={i} className="text-[11px] font-mono bg-white border border-amber-200 rounded px-1.5 py-0.5 text-amber-800">{n.label}: {n.value}</span>))}
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* B1 — "needs your eyes": only the values that need a human (disagree / dup / low-conf / hard-to-read) */}
              {needsEyes.length > 0 && (
                <div data-testid="wb-needs-eyes" className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3">
                  <div className="flex items-center gap-1.5 mb-2">
                    <ListChecks className="w-4 h-4 text-amber-700" />
                    <span className="text-sm font-bold text-amber-900">Needs your eyes ({needsEyes.length})</span>
                  </div>
                  <div className="space-y-1.5">
                    {needsEyes.map((f) => (
                      <div key={f.key} data-testid={`wb-eyes-${f.key}`} className="flex items-center gap-2 flex-wrap bg-white rounded-lg border border-amber-200 px-2 py-1.5">
                        <span className="text-xs font-semibold text-slate-700 w-32 shrink-0">{f.label}</span>
                        <span className="text-xs font-mono text-slate-800">{String(f.value)}</span>
                        {reasonFor(f).map((r, i) => <Badge key={i} className="bg-red-50 text-red-700 border border-red-200 text-[9px]">{r}</Badge>)}
                        {f.ai_photo_ref && <button onClick={() => setPhotoPreview({ fileId: f.ai_photo_ref, label: f.label })} className="text-[10px] underline text-sky-700 inline-flex items-center gap-0.5"><Camera className="w-3 h-3" />photo</button>}
                        <Button data-testid={`wb-eyes-verify-${f.key}`} size="sm" variant="outline" disabled={busyKey === f.key} onClick={() => confirm(f.key)} className="ml-auto h-7 rounded-lg border-emerald-300 text-emerald-700 text-[11px]"><Check className="w-3 h-3 mr-1" />Verify</Button>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-amber-700/80 mt-1.5">Only what disagrees, looks duplicated, is low-confidence, or has hard-to-read characters. Everything else — check each value on its own, one ✓ at a time.</p>
                </div>
              )}

              {/* C1 — full factory option list from the sheet, searchable */}
              {(data?.build_sheet_options || []).length > 0 && (
                <div data-testid="wb-options" className="rounded-xl border border-slate-200 bg-white p-3">
                  <button onClick={() => setShowOptions((s) => !s)} className="w-full flex items-center gap-1.5">
                    <Search className="w-4 h-4 text-slate-500" />
                    <span className="text-sm font-bold text-slate-700">Factory options ({data.build_sheet_options.length})</span>
                    <span className="text-[10px] text-slate-400">searchable — find this truck by option</span>
                    <ChevronRight className={`w-4 h-4 ml-auto text-slate-400 transition-transform ${showOptions ? "rotate-90" : ""}`} />
                  </button>
                  {showOptions && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {/* A factory sheet lists what the PLANT BUILT. An option can have been deleted,
                          swapped or never fitted, so a person can say so per row — the code itself is
                          never edited, it is only marked. */}
                      {data.build_sheet_options.map((o, i) => (
                        <span key={i} className="inline-flex items-center gap-0.5">
                        <button data-testid={`wb-option-${i}`} type="button"
                          title={`${o.group || ""}${o.make ? ` · ${o.make}` : ""}${o.source === "sheet_exact" ? " · read exactly off the sheet" : ""}${o.on_this_truck === false ? ` · marked NOT on this truck by ${o.marked_by}` : ""}\nClick to mark whether it is on this truck`}
                          onClick={() => markOption(o)}
                          className={`text-[10px] border rounded px-1.5 py-0.5 text-left ${o.on_this_truck === false ? "bg-red-50 border-red-200 text-red-700 line-through" : o.source === "sheet_exact" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-slate-100 border-slate-200 text-slate-600"}`}>
                          {o.make ? `${o.make} ` : ""}{o.code ? `${o.code} · ` : ""}{o.description}
                        </button>
                        <AiValueTick donorId={donorId} kind="option" item={o} canEdit={data.can_edit} onDone={loadSpecs} testid={`wb-option-${i}`} />
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Everything-else captured (additional_specs escape hatch) grouped by component + human add/fix */}
              {data && (
                <div data-testid="wb-extras" className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Boxes className="w-4 h-4 text-slate-500" />
                    <span className="text-sm font-bold text-slate-700">Everything else captured — by component</span>
                    <span className="text-[10px] text-slate-400">nothing dropped · click to fix or add</span>
                  </div>
                  <div className="space-y-2">
                    {(data.components || []).filter((c) => (c.extras || []).length > 0).map((c) => (
                      <div key={c.code} data-testid={`wb-extras-${c.code}`}>
                        <span className="text-[11px] font-semibold text-slate-600 capitalize">{c.label}</span>
                        <span className="ml-1.5"><PendingComponentBadge show={c.pending_group} testid={`wb-extras-pending-${c.code}`} /></span>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {c.extras.map((e, i) => (
                            <span key={i} className={`group text-[11px] rounded px-1.5 py-0.5 inline-flex items-center gap-1 border ${e.source === "manual" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                              <b>{e.label}:</b> {e.value}
                              <AiValueTick donorId={donorId} kind="extra" component={c.code} item={e} canEdit={data.can_edit} onDone={loadSpecs} testid={`wb-extra-${c.code}-${i}`} />
                              {data.can_edit && (
                                <select data-testid={`wb-move-extra-${c.code}-${i}`} title="Move to component" value=""
                                  onChange={(ev) => regroup("extra", c.code, ev.target.value, e.value, e.label)}
                                  className="opacity-40 group-hover:opacity-100 bg-transparent text-[10px] border-0 cursor-pointer">
                                  <option value="">move…</option>
                                  {compOptions.filter((o) => o.code !== c.code).map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
                                  <option value="general">General</option>
                                </select>
                              )}
                              <button onClick={() => removeExtra(e)} className="opacity-40 group-hover:opacity-100 hover:text-red-600"><X className="w-3 h-3" /></button>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                    {(data.general_extras || []).length > 0 && (
                      <div data-testid="wb-extras-general">
                        <span className="text-[11px] font-semibold text-slate-600">General</span>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {data.general_extras.map((e, i) => (
                            <span key={i} className={`group text-[11px] rounded px-1.5 py-0.5 inline-flex items-center gap-1 border ${e.source === "manual" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                              <b>{e.label}:</b> {e.value}
                              <AiValueTick donorId={donorId} kind="extra" component="general" item={e} canEdit={data.can_edit} onDone={loadSpecs} testid={`wb-extra-general-${i}`} />
                              <button onClick={() => removeExtra(e)} className="opacity-40 group-hover:opacity-100 hover:text-red-600"><X className="w-3 h-3" /></button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* quick add — the effortless human correction */}
                  {data.can_edit && (
                    <div data-testid="wb-add-extra" className="flex items-center gap-1.5 mt-2 pt-2 border-t border-slate-100 flex-wrap">
                      <select data-testid="wb-add-extra-comp" value={(extraDraft._add?.component) || "general"}
                        onChange={(e) => setExtraDraft((d) => ({ ...d, _add: { ...(d._add || {}), component: e.target.value } }))}
                        className="h-8 rounded-lg border border-slate-200 text-xs px-1.5">
                        <option value="general">General</option>
                        {(data.components || []).map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                      </select>
                      <Input data-testid="wb-add-extra-label" placeholder="Label (e.g. Transfer Case)" value={extraDraft._add?.label || ""}
                        onChange={(e) => setExtraDraft((d) => ({ ...d, _add: { ...(d._add || {}), label: e.target.value } }))} className="h-8 w-40 rounded-lg text-xs" />
                      <Input data-testid="wb-add-extra-value" placeholder="Value" value={extraDraft._add?.value || ""}
                        onChange={(e) => setExtraDraft((d) => ({ ...d, _add: { ...(d._add || {}), value: e.target.value } }))} className="h-8 w-36 rounded-lg text-xs" />
                      <Button data-testid="wb-add-extra-btn" size="sm" onClick={addExtra} className="h-8 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-xs">Add</Button>
                    </div>
                  )}
                </div>
              )}

              {/* field review */}
              {specGroups.map((g) => (
                <div key={g.group} data-testid={`wb-group-${g.group}`}>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">{g.group}</div>
                  <div className="space-y-1.5">
                    {g.fields.map((f) => (
                      <div key={f.key} data-testid={`wb-field-${f.key}`} className="py-1 border-b border-slate-50">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="w-36 shrink-0 text-sm text-slate-600">{f.label}</span>
                          {renderInput(f)}
                          {dirty(f) && <Button size="sm" onClick={() => saveVal(f.key, draft[f.key])} disabled={busyKey === f.key} className="h-7 rounded-lg bg-[#E0781A] text-[11px]">Save</Button>}
                          {sourceBadge(f)}
                          {chip(f)}
                          {f.dup_warning && <Badge data-testid={`wb-dup-${f.key}`} className="bg-red-50 text-red-700 border border-red-200 text-[10px]"><AlertTriangle className="w-3 h-3 mr-0.5" />also on {f.dup_warning}</Badge>}
                          {f.value != null && f.value !== "" && f.unverified && (
                            <Button data-testid={`wb-confirm-${f.key}`} size="sm" variant="outline" disabled={busyKey === f.key} onClick={() => confirm(f.key)} className="h-7 rounded-lg border-emerald-300 text-emerald-700 text-[11px]"><Check className="w-3 h-3 mr-1" />Confirm</Button>
                          )}
                          {f.ai_hint && (
                            <span data-testid={`wb-hint-${f.key}`} className="inline-flex items-center gap-1 text-[11px] text-indigo-600"><Wand2 className="w-3 h-3" />AI saw <b>{String(f.ai_hint.value)}</b><button onClick={() => applyHint(f.key, f.ai_hint.value)} className="underline">use</button></span>
                          )}
                        </div>
                        {provenanceRow(f)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* THE MOMENT THE DATA GETS ENTERED. He is already looking at the record and the
                  photos; what is still ON the truck is the other half of the same look. IT IS
                  HERE **AND** ON THE TRUCK PAGE, on purpose (owner ruling 2026-09-25): he does
                  this alongside the AI scrub, so it has to be under his hand in here — but it must
                  NOT depend on the AI switch, so the truck page carries the same block for the
                  days AI is off, for accounts without `use_ai_assist`, and for the worklist tile
                  that sends him straight to it. Same endpoint, same fact, and a press in here
                  reloads the panel behind it so the two can never show different answers. */}
              {data && <PresenceReviewBlock donorId={donorId} data={data}
                onSaved={() => loadSpecs()}
                onGoToSpecs={(code) => {
                  onDone?.(); onOpenChange(false);
                  setTimeout(() => document.querySelector(`[data-testid="spec-component-${code}"]`)
                    ?.scrollIntoView({ behavior: "smooth", block: "center" }), 350);
                }} />}

              <div className="pt-2">
                <Button data-testid="wb-done" onClick={() => { onDone?.(); onOpenChange(false); }} className="w-full h-10 rounded-lg bg-slate-800 hover:bg-slate-900 text-white">Done</Button>
              </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {cpt && (
        <CreatePartFromTruck open={!!cpt} onOpenChange={(o) => !o && setCpt(null)} donorId={donorId} donorStock={donor?.stock_number}
          component={cpt.component} componentNumbers={cpt.componentNumbers}
          specKey={cpt.specKey} suggestedName={cpt.suggestedName} serialValue={cpt.serialValue}
          onCreated={() => { setCpt(null); loadSpecs(); onDone?.(); }} />
      )}

      {/* COMPARE — original build sheet (a) ↔ normalized transcript (b). Verify every serial matches before spawning. */}
      <Dialog open={showCompare} onOpenChange={setShowCompare}>
        <DialogContent data-testid="wb-compare-dialog" className="max-w-6xl w-[95vw] h-kb p-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-4 py-3 border-b border-slate-200 shrink-0">
            <DialogTitle className="flex items-center gap-2 text-base"><Columns2 className="w-4 h-4 text-indigo-600" />Original ↔ Normalized — check every serial matches</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 flex-1 min-h-0">
            <div className="border-r border-slate-200 overflow-auto p-3">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">a · Original upload (raw)</div>
              {donor?.build_sheet?.file_id
                ? <BuildSheetViewer buildSheet={donor.build_sheet} testid="wb-compare-original" />
                : <div className="text-sm text-slate-400 p-6 text-center">No original file on this truck.</div>}
            </div>
            <div className="overflow-auto p-3 bg-slate-50">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">b · Normalized transcript (what the AI read)</div>
              <pre data-testid="wb-compare-transcript" className="text-[11px] leading-snug font-mono text-slate-700 whitespace-pre-wrap">{normalizedText || "Normalize the build sheet first."}</pre>
            </div>
          </div>
          <div className="px-4 py-2 border-t border-slate-200 shrink-0 text-[11px] text-slate-500 flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5" />The transcript is a faithful copy — nothing invented. If a serial here doesn't match the original, fix it in the fields before you spawn a part off it. Placed values (c) are in the review list behind this dialog.
          </div>
        </DialogContent>
      </Dialog>

      {/* A1/A2 — THE EXACT PHOTO A VALUE WAS READ FROM, in the one viewer. It used to be a plain
          image in a dialog that told the reader to "zoom in and confirm each character" — over a
          photo that could not be zoomed, rotated or panned (his instruction, 2026-10-15). */}
      <PhotoLightbox photos={photoPreview?.fileId ? [{ file_id: photoPreview.fileId }] : []}
        index={photoPreview?.fileId ? 0 : null} onIndex={() => {}}
        onClose={() => setPhotoPreview(null)} testidPrefix="wb-photo-preview" />

      {/* THE WHOLE STRIP, full screen, from the stage. */}
      <PhotoLightbox photos={photos} index={fullIdx} onIndex={setFullIdx}
        onClose={() => setFullIdx(null)} testidPrefix="wb-photo-full" />
    </>
  );
}
